import type { components } from '@sportik/api-coach/schema';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api-client';

type ProgramList = components['schemas']['ProgramList'];

/**
 * The program a client is on now: the published one, alone. The list gives
 * its id; the plan comes with `programOptions`.
 */
export function currentProgramOptions(clientId: string) {
  return api.queryOptions('get', '/clients/{clientId}/programs', {
    params: { path: { clientId }, query: { state: ['published'], limit: 1 } },
  });
}

/** One program with its plan: routines, length, and the week it is in. */
export function programOptions(clientId: string, programId: string) {
  return api.queryOptions('get', '/clients/{clientId}/programs/{programId}', {
    params: { path: { clientId, programId } },
  });
}

// `null` says the read is whole and the client is on no program.
const toCurrentId = (list: ProgramList) => list.items[0]?.id ?? null;

/**
 * The client's current program with its plan, in two reads: which program,
 * then the program. `program` is `null` when the client has none and
 * `undefined` while either read is on the way; `error` and `refetch` belong
 * to the read that failed.
 */
export function useCurrentProgram(clientId: string) {
  const current = useQuery({
    ...currentProgramOptions(clientId),
    select: toCurrentId,
  });
  const program = useQuery({
    ...programOptions(clientId, current.data ?? ''),
    enabled: Boolean(current.data),
  });
  const failed = current.error ? current : program;
  return {
    program: current.data === null ? null : program.data,
    error: failed.error,
    refetch: failed.refetch,
  };
}

/** The one definition of this read: what happened to the client's programs, newest first. */
export function programHistoryOptions(clientId: string) {
  return api.queryOptions('get', '/clients/{clientId}/program-history', {
    params: { path: { clientId }, query: { limit: 200 } },
  });
}

/** Every assignment, edit, replacement, and end of the client's programs, newest first. */
export function useProgramHistory(clientId: string) {
  return useQuery(programHistoryOptions(clientId));
}
